import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, NewsPost, I18nText } from '../../lib/supabase';
import Layout from '../../components/feature/Layout';

type Lang = 'ko' | 'en' | 'vi';

const getI18n = (obj: I18nText | undefined, lang: Lang): string => {
  if (!obj) return '';
  return obj[lang] || obj.ko || obj.en || obj.vi || '';
};

const formatDate = (dateStr: string | null, lang: Lang): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const locale = lang === 'ko' ? 'ko-KR' : lang === 'vi' ? 'vi-VN' : 'en-US';
  return d.toLocaleDateString(locale, opts);
};

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) as Lang) || 'ko';
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState<string>('');

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('news_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      setPosts((data as NewsPost[]) || []);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const allTags = Array.from(new Set(posts.flatMap((p) => p.tags || [])));
  const filtered = activeTag ? posts.filter((p) => p.tags?.includes(activeTag)) : posts;

  return (
    <Layout>
      <title>{t('page.title.news')}</title>

      {/* Hero */}
      <section
        className="relative min-h-[340px] flex items-end pb-14 pt-36 overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/18871237539a70f89958b883af092a01.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/80" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans tracking-[0.25em] uppercase text-silver/80 mb-3">
            {t('newsPage.eyebrow')}
          </p>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            {t('newsPage.heading1')}{' '}
            <span className="text-silver/70">{t('newsPage.heading2')}</span>
          </h1>
          <p className="text-white/70 font-sans text-sm lg:text-base max-w-xl leading-relaxed">
            {t('newsPage.heroDesc')}
          </p>
        </div>
      </section>

      {/* Tag Filter */}
      <section className="bg-white border-b border-gray-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTag('')}
            className={`px-4 py-1.5 rounded-full text-xs font-sans font-medium transition-all whitespace-nowrap cursor-pointer ${
              activeTag === '' ? 'bg-navy text-white' : 'bg-gray-100 text-navy/60 hover:bg-gray-200'
            }`}
          >
            {t('newsPage.all')}
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTag === tag ? 'bg-navy text-white' : 'bg-gray-100 text-navy/60 hover:bg-gray-200'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-[#F8F9FB] min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="w-full h-52 bg-gray-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-5 bg-gray-200 rounded w-4/5" />
                    <div className="h-5 bg-gray-200 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-full" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <i className="ri-newspaper-line text-5xl text-navy/20 block mb-4" />
              <p className="text-navy/40 font-sans text-sm">{t('newsPage.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, idx) => (
                <NewsCard key={post.id} post={post} lang={lang} idx={idx} t={t} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

interface CardProps {
  post: NewsPost;
  lang: Lang;
  idx: number;
  t: (key: string) => string;
}

function NewsCard({ post, lang, t }: CardProps) {
  const title = getI18n(post.title_i18n, lang);
  const summary = getI18n(post.summary_i18n, lang);
  const dateStr = formatDate(post.published_at, lang);

  return (
    <Link
      to={`/news/${post.slug}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
      className="group bg-white rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-transform duration-300 hover:-translate-y-1 border border-gray-100"
    >
      {/* Cover */}
      <div className="w-full h-52 bg-[#E8ECF2] overflow-hidden flex-shrink-0">
        {post.cover_image_url ? (
          <img
            src={post.cover_image_url}
            alt={title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy/10 to-navy/5">
            <i className="ri-newspaper-line text-4xl text-navy/20" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6">
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-0.5 bg-navy/5 text-navy/60 text-[10px] font-sans font-semibold rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h2 className="font-serif text-navy text-lg font-bold leading-snug mb-2 line-clamp-2 group-hover:text-navy/80 transition-colors">
          {title || 'Untitled'}
        </h2>

        {/* Summary */}
        <p className="text-navy/50 font-sans text-sm leading-relaxed line-clamp-2 flex-1 mb-4">
          {summary}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-navy/40 text-xs" />
            </div>
            <div>
              {post.author && (
                <p className="text-[11px] font-sans text-navy/60 leading-none">{post.author}</p>
              )}
              {dateStr && (
                <p className="text-[10px] font-sans text-navy/35 mt-0.5">{dateStr}</p>
              )}
            </div>
          </div>
          <span className="text-xs font-sans font-semibold text-navy/50 group-hover:text-navy transition-colors flex items-center gap-1 whitespace-nowrap">
            {t('newsPage.readMore')} <i className="ri-arrow-right-line text-sm" />
          </span>
        </div>
      </div>
    </Link>
  );
}
